export const DEFAULT_TZ = 'Europe/Moscow';
const NBSP = ' ';
/** 2490000 коп. → «24 900 ₽». Копейки показываются, только если они есть. */
export function formatMoney(kop, opts = {}) {
    if (kop === null || kop === undefined)
        return opts.empty ?? '—';
    const negative = kop < 0;
    const abs = Math.abs(Math.round(kop));
    const rub = Math.floor(abs / 100);
    const k = abs % 100;
    const grouped = String(rub).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    const body = k ? `${grouped},${String(k).padStart(2, '0')}` : grouped;
    return `${negative ? '−' : ''}${body}${opts.currency === false ? '' : `${NBSP}₽`}`;
}
/** «24 900» / «24900,50» → копейки. Пустая строка → null. */
export function parseMoney(input) {
    if (input === null || input === undefined)
        return null;
    if (typeof input === 'number')
        return Math.round(input * 100);
    const clean = input.replace(/[\s₽]/g, '').replace(',', '.');
    if (!clean)
        return null;
    const n = Number(clean);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
}
function parts(date, tz) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const f = new Intl.DateTimeFormat('ru-RU', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
    return p;
}
/** «12.10» */
export function formatDay(date, tz = DEFAULT_TZ) {
    if (!date)
        return '—';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [, m, d] = date.split('-');
        return `${d}.${m}`;
    }
    const p = parts(date, tz);
    return `${p.day}.${p.month}`;
}
/** «12.10.2026» */
export function formatDate(date, tz = DEFAULT_TZ) {
    if (!date)
        return '—';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [y, m, d] = date.split('-');
        return `${d}.${m}.${y}`;
    }
    const p = parts(date, tz);
    return `${p.day}.${p.month}.${p.year}`;
}
/** «12.10 09:40» */
export function formatDayTime(date, tz = DEFAULT_TZ) {
    if (!date)
        return '—';
    const p = parts(date, tz);
    return `${p.day}.${p.month} ${p.hour}:${p.minute}`;
}
/** Сегодняшняя дата в часовом поясе мастерской: «2026-10-12» */
export function todayIso(tz = DEFAULT_TZ, now = new Date()) {
    const p = parts(now, tz);
    return `${p.year}-${p.month}-${p.day}`;
}
/** Название месяца в именительном падеже: «октябрь» */
export function monthName(date, tz = DEFAULT_TZ) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('ru-RU', { timeZone: tz, month: 'long' }).format(d);
}
export function pluralRu(n, forms) {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20)
        return forms[2];
    if (b > 1 && b < 5)
        return forms[1];
    if (b === 1)
        return forms[0];
    return forms[2];
}
/** Любой ввод телефона → «+79991234567». null — если цифр не 11 (или 10 без кода страны). */
export function normalizePhone(input) {
    if (!input)
        return null;
    let digits = input.replace(/\D/g, '');
    if (digits.length === 10)
        digits = '7' + digits;
    if (digits.length === 11 && digits.startsWith('8'))
        digits = '7' + digits.slice(1);
    if (digits.length !== 11)
        return null;
    return '+' + digits;
}
/** «+79991234567» → «+7 (999) 123-45-67» */
export function formatPhone(phone) {
    if (!phone)
        return '—';
    const d = phone.replace(/\D/g, '');
    if (d.length !== 11)
        return phone;
    return `+${d[0]} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}
/** «+7 (9••) •••-45-12» — для экранов, где полный номер не нужен */
export function maskPhone(phone) {
    if (!phone)
        return '—';
    const d = phone.replace(/\D/g, '');
    if (d.length !== 11)
        return phone;
    return `+${d[0]} (${d[1]}••) •••-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}
/** «tel:+79991234567» */
export function telHref(phone) {
    const d = (phone ?? '').replace(/[^\d+]/g, '');
    return `tel:${d}`;
}
const LATIN_TO_CYR = {
    A: 'А',
    B: 'В',
    E: 'Е',
    K: 'К',
    M: 'М',
    H: 'Н',
    O: 'О',
    P: 'Р',
    C: 'С',
    T: 'Т',
    Y: 'У',
    X: 'Х',
};
/** Госномер: верхний регистр, латиница-двойники → кириллица, одиночные пробелы. «a412ok77» → «А412ОК77» */
export function normalizePlate(input) {
    if (!input)
        return '';
    return input
        .toUpperCase()
        .replace(/[ABEKMHOPCTYX]/g, (c) => LATIN_TO_CYR[c] ?? c)
        .replace(/\s+/g, ' ')
        .trim();
}
/** OEM-номер для поиска: только буквы и цифры в верхнем регистре. */
export function normalizeOem(input) {
    if (!input)
        return '';
    return input.toUpperCase().replace(/[^0-9A-ZА-ЯЁ]/g, '');
}
/** «Р-2610-0187» → «0187» — короткий номер для бирок и списков на телефоне */
export function shortNumber(num) {
    const m = num.match(/(\d+)$/);
    return m ? m[1] : num;
}
const YANDEX_HOST = /(^|\.)yandex\.[a-z]+$/;
/**
 * Ссылка на карту для iframe. Страницы yandex.ru/maps и скрипт конструктора Яндекс запрещает показывать во фрейме
 * (X-Frame-Options: DENY) — вместо карты белый прямоугольник. Поэтому код iframe или script из конструктора,
 * ссылка «Поделиться» и обычная ссылка на Яндекс Карты приводятся к виджету yandex.ru/map-widget/v1.
 */
export function toMapEmbedUrl(input) {
    const text = input?.trim();
    if (!text)
        return null;
    const src = (/\bsrc\s*=\s*["']([^"']+)["']/i.exec(text)?.[1] ?? text).replaceAll('&amp;', '&');
    let url;
    try {
        url = new URL(src);
    }
    catch {
        return src;
    }
    if (!YANDEX_HOST.test(url.hostname))
        return src;
    const um = url.searchParams.get('um');
    if (um?.startsWith('constructor:'))
        return `https://yandex.ru/map-widget/v1/?um=${encodeURIComponent(um)}&source=constructor`;
    const path = /^\/maps(\/.*)?$/.exec(url.pathname);
    if (path)
        return `${url.origin}/map-widget/v1${path[1] ?? '/'}${url.search}`;
    return src;
}
/**
 * Координаты так, как их показывают Яндекс Карты: «55.796127, 49.106414» (широта, долгота) → «55.796127,49.106414».
 * Принимает десятичную запятую и разделители «,», «;», пробел. null — если это не координаты.
 */
export function normalizeGeoPoint(input) {
    const m = /^\s*(-?\d{1,2}(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:[.,]\d+)?)\s*$/.exec(input ?? '');
    if (!m)
        return null;
    const lat = Number(m[1].replace(',', '.'));
    const lon = Number(m[2].replace(',', '.'));
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180)
        return null;
    return `${lat},${lon}`;
}
/** «долгота,широта» из параметров Яндекс Карт (pt, whatshere[point], ll) → «широта,долгота» */
function fromLonLat(v) {
    const m = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(v ?? '');
    return m ? normalizeGeoPoint(`${m[2]},${m[1]}`) : null;
}
/**
 * Точка площадки из того, что вставил человек: координаты («55.79, 49.10») или ссылка на Яндекс Карты.
 * Координаты берутся из метки (pt), «Что здесь?» (whatshere[point]), конца маршрута (rtext) и центра карточки
 * организации (ll); у поиска центр карты — не точка. Короткая ссылка «Поделиться» (/maps/-/…) остаётся как есть:
 * по ней карта откроет ту же точку. null — это не точка на Яндекс Картах.
 */
export function parseMapPoint(input) {
    const text = input?.trim() ?? '';
    const coords = normalizeGeoPoint(text);
    if (coords)
        return { geoPoint: coords, mapUrl: null };
    const raw = /https?:\/\/\S+/i.exec(text)?.[0];
    if (!raw)
        return null;
    let url;
    try {
        url = new URL(raw);
    }
    catch {
        return null;
    }
    if (!YANDEX_HOST.test(url.hostname) || !(url.hostname.startsWith('maps.') || url.pathname.startsWith('/maps')))
        return null;
    const p = url.searchParams;
    const routeEnd = p.get('rtext')?.split('~').pop();
    const geoPoint = fromLonLat(p.get('pt')) ??
        fromLonLat(p.get('whatshere[point]')) ??
        (routeEnd ? normalizeGeoPoint(routeEnd) : null) ??
        (url.pathname.includes('/org/') ? fromLonLat(p.get('ll')) : null);
    return { geoPoint, mapUrl: raw };
}
/** Поиск адреса в Яндекс Картах */
export const yandexSearchUrl = (address) => `https://yandex.ru/maps/?text=${encodeURIComponent(address)}`;
/** Куда отправить водителя: маршрут до точки, иначе ссылка с карты, иначе поиск адреса */
export function yandexNavUrl(p) {
    if (p.geoPoint)
        return yandexRouteUrl(p.geoPoint);
    if (p.mapUrl)
        return p.mapUrl;
    return p.address?.trim() ? yandexSearchUrl(p.address.trim()) : null;
}
/** Маршрут в Яндекс Картах от того места, где человек сейчас, до точки. На телефоне ссылки yandex.ru/maps открывает приложение. */
export const yandexRouteUrl = (geoPoint) => `https://yandex.ru/maps/?rtext=~${geoPoint}&rtt=auto`;
/** Виджет карты с меткой в точке «широта,долгота». В параметре pt Яндекса — сначала долгота; карта центрируется по метке. */
export function yandexPointMapUrl(geoPoint) {
    const [lat, lon] = geoPoint.split(',');
    return `https://yandex.ru/map-widget/v1/?pt=${lon},${lat}&z=16`;
}
//# sourceMappingURL=format.js.map